/**
 * Centralized configuration for listings feed UI.
 *
 * Single source of truth for score tiers, signal color thresholds,
 * and behavioral constants shared across ListingCard, ListingDetailPopup,
 * ListingCompare, ListingsFeed, and useListingsFeed.
 */

/** Signal value color thresholds */
export const SIGNAL_COLOR_THRESHOLDS = {
  good: 70,
  ok: 40,
};

/** Get a signal value color: green for good (>=70), amber for OK (40-69), grey for low (<40). */
export const getSignalColor = (value) => {
  if (value >= SIGNAL_COLOR_THRESHOLDS.good) return '#16A34A';
  if (value >= SIGNAL_COLOR_THRESHOLDS.ok) return '#F59E0B';
  return '#9CA3AF';
};

/** Duration in ms to show the undo toast before committing a dismiss */
export const UNDO_TOAST_DURATION_MS = 5000;

/** Feed is considered stale after this many ms (24 hours) */
export const STALE_FEED_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// NEW ENTRY image badge fires when a listing was first surfaced within this
// window AND the user hasn't viewed it. 3 days lands at ~11% prevalence in
// prod (sits between TOP MATCH 5% and BEST DEAL 12%); gives unviewed users a
// couple of opens to catch fresh feed entries before they age out.
export const NEW_ENTRY_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Redesign V2 — 3-tier score system, signal labels, and advice text
// ---------------------------------------------------------------------------

export const REDESIGN_SCORE_TIERS = [
  { min: 70, color: '#3AE869', bgColor: 'rgba(58,232,105,0.2)', label: 'Good' },
  { min: 30, color: '#FF7B00', bgColor: 'rgba(255,123,0,0.2)', label: 'Medium' },
  { min: 0, color: '#FF0000', bgColor: 'rgba(255,0,0,0.2)', label: 'Poor' },
];

export const getRedesignScoreTier = (score) => {
  const numericScore = Number(score) || 0;
  for (const tier of REDESIGN_SCORE_TIERS) {
    if (numericScore >= tier.min) return tier;
  }
  return REDESIGN_SCORE_TIERS[REDESIGN_SCORE_TIERS.length - 1];
};

// Neutral category labels — describe the topic, never the verdict. The
// right-column SignalTagBadge carries the valence; the left-column secondary
// label must not contradict the tag when data goes against the positive case.
export const SIGNAL_FRIENDLY_LABELS = {
  price_vs_market: 'Price',
  distance: 'Distance',
  mileage: 'Mileage',
  trim_match: 'Trim',
  dom: 'Time on market',
  year_match: 'Year',
  price_trend: 'Price trend',
  transmission_match: 'Transmission',
  fuel_match: 'Fuel',
  owners: 'Previous keepers',
  ulez: 'Emissions',
};

export const HIDDEN_SIGNALS = new Set([]);

/**
 * Parse the price-vs-market delta from a listing's score_breakdown.
 *
 * The scoring engine writes a human explanation on the `price_vs_market` signal
 * (e.g. "£1,234 below market average" / "£800 above market average"). This is the
 * authoritative per-listing money delta: it was computed against the correct
 * market segment at feed-generation time. Reused across the feed card money pill,
 * the first-session hero, and the listing-detail price anchor so the parsing logic
 * lives in one place.
 *
 * @param {Array} scoreBreakdown - score_breakdown array from a feed entry.
 * @param {number} [minAmount=200] - Minimum absolute £ delta to report (filters noise).
 * @returns {{ direction: 'below'|'above', amount: number, label: string }|null}
 */
export const parsePriceVsMarket = (scoreBreakdown, minAmount = 200) => {
  if (!Array.isArray(scoreBreakdown)) return null;
  const signal = scoreBreakdown.find((s) => s?.signal === 'price_vs_market');
  const explanation = signal?.explanation || '';
  const match = explanation.match(/£([\d,]+)\s+(below|above)/i);
  if (!match) return null;
  const amount = parseInt(match[1].replace(/,/g, ''), 10);
  if (!Number.isFinite(amount) || amount < minAmount) return null;
  const direction = match[2].toLowerCase() === 'above' ? 'above' : 'below';
  const formatted = `£${amount.toLocaleString('en-GB')}`;
  return {
    direction,
    amount,
    label: direction === 'below'
      ? `${formatted} under market`
      : `${formatted} above market`,
  };
};

// Cutoffs aligned to the OverallMatchBadge tier bands in scoreTags.js (72/63/50)
// so the conversational advice text never disagrees with the badge tier.
export const ADVICE_TEXT = [
  { min: 72, text: 'You should consider it' },                  // Strong match
  { min: 63, text: 'Worth negotiating' },                       // Solid match
  { min: 50, text: 'Some trade-offs, negotiate carefully' },    // Mixed match
  { min: 0,  text: 'Probably one to skip' },                    // Limited match
];
